/**
 * Typed wrappers for the public surface, `/api/v1/public` (plus the Phase 1 aliases `/c/:code`
 * and `/claims/:rawToken/*` the backend still serves).
 *
 * Every call here is anonymous: no Authorization header is attached, so a signed-in operator
 * looking at a public page sees exactly what a visitor sees.
 *
 * The raw claim token appears only as the path parameter of the request that must carry it. It is
 * never stored, never put in a query string, never logged and never written to analytics.
 */
import type { ApiClient } from './client';
import type { ClaimPreview, CredentialSummary, PublicLinkedInShare, PublicView } from './types';

const BASE = '/api/v1/public';

export const getPublicCredential = (api: ApiClient, code: string) =>
  api.json<PublicView>(`${BASE}/credentials/${encodeURIComponent(code)}`, { anonymous: true });

export const getPublicLinkedIn = (api: ApiClient, code: string) =>
  api.json<PublicLinkedInShare>(`${BASE}/credentials/${encodeURIComponent(code)}/linkedin`, {
    anonymous: true,
  });

/** Artifact URL for an `<img>`/`<a download>`; the route is public and needs no header. */
export const publicArtifactUrl = (api: ApiClient, code: string, kind: 'svg' | 'png'): string =>
  `${api.baseUrl}${BASE}/credentials/${encodeURIComponent(code)}/artifacts/${kind}`;

export const downloadPublicArtifact = (api: ApiClient, code: string, kind: 'svg' | 'png') =>
  api.download(`${BASE}/credentials/${encodeURIComponent(code)}/artifacts/${kind}`, { anonymous: true });

/**
 * The stored SVG as text, for the page that draws it inline.
 *
 * Rule 10: what a visitor sees is the artifact the render manifest was hashed over, not a picture
 * the browser reproduced from the design. It goes through the client like everything else — no
 * component calls `fetch` itself (`scripts/check-invariants.mjs`), so the bearer rule, the error
 * normalisation and the 401 hook all stay in one place.
 */
export const getPublicArtifactSvg = async (
  api: ApiClient,
  code: string,
  /**
   * Which sealed language's document, or absent for the record's own.
   *
   * One narrow query parameter on the address this download already has: the canonical credential
   * URL is unchanged and there is no second route family. An unsupported value is refused by the
   * server rather than rounded down to a language it does have.
   */
  lang?: 'tr' | 'en',
): Promise<string> => {
  const query = lang ? `?lang=${lang}` : '';
  const r = await api.download(`${BASE}/credentials/${encodeURIComponent(code)}/artifacts/svg${query}`, {
    anonymous: true,
  });
  return r.blob.text();
};

/**
 * The same bytes for the RECIPIENT's page, gated by the one-time token rather than by nothing.
 *
 * A waiting record has no public artifact — `/c/:code/artifacts/svg` answers 404 for it on
 * purpose (rule 12) — so the claim page asks on the route its own token authorises. The token is
 * the path parameter of the request that must carry it and goes nowhere else.
 */
export const getClaimArtifactSvg = async (
  api: ApiClient,
  rawToken: string,
  /**
   * Which sealed language's document, or absent for the record's own.
   *
   * The same one narrow query parameter the public download takes, on the address the claim already
   * has: no second route family, and an unsupported value is refused by the server rather than
   * rounded down to a language it does have. The token stays where it was, in the path.
   */
  lang?: 'tr' | 'en',
): Promise<string> => {
  const query = lang ? `?lang=${lang}` : '';
  const r = await api.download(`${BASE}/claims/${encodeURIComponent(rawToken)}/artifact.svg${query}`, {
    anonymous: true,
  });
  return r.blob.text();
};

export const previewClaim = (api: ApiClient, rawToken: string) =>
  api.json<ClaimPreview>(`${BASE}/claims/${encodeURIComponent(rawToken)}`, { anonymous: true });

export const acceptClaim = (api: ApiClient, rawToken: string) =>
  api.json<{ credential: CredentialSummary }>(`${BASE}/claims/${encodeURIComponent(rawToken)}/accept`, {
    method: 'POST',
    anonymous: true,
  });

export const declineClaim = (api: ApiClient, rawToken: string) =>
  api.json<{ credential: CredentialSummary }>(`${BASE}/claims/${encodeURIComponent(rawToken)}/decline`, {
    method: 'POST',
    anonymous: true,
  });

/** `GET /health` — used by the offline/backend-down banner, not by any screen's data path. */
/**
 * Reports that the visitor STARTED an action that leaves this page — nothing more.
 *
 * The route admits exactly two kinds and neither of them can become a publication: what a browser
 * sends is an anonymous claim about an unidentified visitor, which is enough to say «this was
 * started» and nothing else. Only the server, holding LinkedIn's own answer, can record a post.
 *
 * Deliberately fire-and-forget at the call site: the navigation the visitor asked for must not wait
 * for this, and a failure here must not cost them the link.
 */
export const recordPublicEngagement = (
  api: ApiClient,
  code: string,
  kind: 'linkedin_profile_add_started' | 'linkedin_share_started',
) =>
  api.json<void>(`${BASE}/credentials/${encodeURIComponent(code)}/engagement`, {
    method: 'POST',
    body: { kind },
    anonymous: true,
  });

/**
 * The acts the product performs in the browser and nowhere else.
 *
 * Every kind here is named at the level it is observed at — a print WINDOW opening, a download
 * REQUEST, a link copied — and the server's allow-list is the same one, so nothing sent from here
 * can become a system record or a verified success.
 *
 * `key` names ONE interaction and travels with its retries, so a lost response is safe to resend
 * and still one row; two genuine presses carry two keys and stay two. Fire-and-forget at every call
 * site: the certificate opens, downloads and prints whether or not this arrives.
 */
export type PublicInteractionKind =
  | 'public_viewed'
  | 'claim_opened'
  | 'downloaded'
  | 'printed'
  | 'linkedin_helper_opened'
  | 'linkedin_share_opened'
  | 'og_preview_opened'
  | 'link_copied'
  | 'text_copied';

export const recordPublicInteraction = (
  api: ApiClient,
  code: string,
  kind: PublicInteractionKind,
  key?: string,
) =>
  api.json<void>(`${BASE}/credentials/${encodeURIComponent(code)}/telemetry`, {
    method: 'POST',
    body: { kind, ...(key ? { key } : {}) },
    anonymous: true,
  });

export const health = (api: ApiClient) => api.json<{ status: string }>('/health', { anonymous: true });
